import { Data, DateUtils, List, Misc } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import DividendSummaryCriteria from '../../Models/Dividends/Lookups/DividendSummaryCriteria';
import DividendSummaryLookup from '../../Models/Dividends/Lookups/DividendSummaryLookup';
import DividendAccrualDetailCriteria from '../../Models/Dividends/Lookups/DividendAccrualDetailCriteria';
import DividendAccrualDetailLookup from '../../Models/Dividends/Lookups/DividendAccrualDetailLookup';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import { DividendAccrualDetailInfo } from '../../Models/Dividends/Lookups/DividendAccrualDetailResponse';
import TextValues from '../../Services/TextValues';

export default class DividendProcessingVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        protected dividendProcessingApiClient = AppService.get(Types.Domain.ApiClients.DividendProcessing)) {
        super(taskRunner);
        this.makeObservable();
        this.autoDispose(this.summaryCriteria.onAnyPropertyChanged(() => {
            if (this.summaryCriteria.startDate && this.summaryCriteria.entityId && this.summaryCriteria.endDate) {
                this.loadSummary();
            }
        }));
    }

    public summaryCriteria = new DividendSummaryCriteria();
    public dividendSummaryList = new List(DividendSummaryLookup);
    public accrualDetailCriteria = new DividendAccrualDetailCriteria();
    public currentlyProcessing: DividendSummaryLookup | null = null;
    public currentInfo: DividendAccrualDetailInfo | null = null;

    public pageManager = new Data.PageManager(this.accrualDetailCriteria, DividendAccrualDetailLookup, this.dividendProcessingApiClient.getDividendAccrualDetailPage, {
        pageSize: 20,
        initialTaskRunner: this.taskRunner,
        fetchInitial: false,
        allowSort: true,
    });

    public async initialise() {
        this.summaryCriteria.startDate = DateUtils.add(new Date(), { days: -14 });
        this.summaryCriteria.endDate = DateUtils.add(new Date(), { months: 1 });
        const entities = await this.dataCache.entities.getDataAsync();
        this.summaryCriteria.entityId = entities.find(entity => entity.hasVestingTrust === true)?.entityId ?? null;
    }

    public async loadSummary() {
        const response = await this.taskRunner.waitFor(this.dividendProcessingApiClient.getDividendSummaries(this.summaryCriteria.toQueryObject()));
        this.dividendSummaryList.set(response.data);
    }
    
    public async loadAccrualDetails(processingItem: DividendSummaryLookup) {
        this.accrualDetailCriteria.startDate = processingItem.startDate;
        this.accrualDetailCriteria.entityId = this.summaryCriteria.entityId;

        const response = await this.taskRunner.waitForData(this.dividendProcessingApiClient.getDividendAccrualDetail(this.accrualDetailCriteria.toQueryObject()));
        const firstPage = response.page!;
        
        this.currentInfo = DividendAccrualDetailInfo.fromJSObject<DividendAccrualDetailInfo>(response.info!);
        this.pageManager.setData(firstPage.entityList, firstPage.total);

        this.currentlyProcessing = processingItem;
    }

    public async processDividendAccruals() {
        const response = await this.taskRunner.run(async () => this.dividendProcessingApiClient.processDividendAccruals(this.accrualDetailCriteria.toJSObject()))
        this.currentlyProcessing = null;
        this.accrualDetailCriteria.startDate = null;
        this.accrualDetailCriteria.entityId = null;
        this.pageManager.reset();
        await this.loadSummary();
        this.notifications.addSuccess(`${TextValues.distribution} Processing Successful`, `${TextValues.distribution}s were generated successfully.`, NotificationDuration.Standard);
    }

    public back() {
        this.currentlyProcessing = null;
        this.accrualDetailCriteria.startDate = null;
        this.accrualDetailCriteria.entityId = null;
        this.pageManager.reset();
    }
}